import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { Formation } from '@shared/models/formation.model';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { Personas } from '@shared/models/personas.model';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { UserService } from '@shared/services/user.service';
import { NavigationService } from '../../../core/services/navigation.service';
import { Paramedical } from '../../models/paramedical.model';
import { Profile } from '../../models/profile.model';
import { ErrorService } from '../../services/error.service';
import { ParamedicalService } from '../../services/paramedical.service';
import { ProfileService } from '../../services/profile.service';
import { InstanceIdService } from '@shared/services/instance-id.service';

type ExtendedProfile = Profile & {
  shouldDisableProfileButton?: boolean;
  shouldDisableSmartplanningButton?: boolean;
  shouldDisableSkillsButton?: boolean;
  shouldDisableStatopButton?: boolean;
  shouldDisableJobDescriptionButton?: boolean;
};

@Component({
  selector: 'app-default-popup',
  templateUrl: './annuaire-popup.component.html',
  styleUrls: ['./annuaire-popup.component.scss'],
  standalone: false,
})
export class AnnuairePopupComponent implements OnInit {
  private instanceId: string;

  private pageSize = 10;

  private hasNextPage: any;

  private page = 1;

  public searchValue = '';

  public isLoading = true;

  public profiles: ExtendedProfile[] | undefined;

  public paramedicalsData: Paramedical[] | undefined;

  public scrollLoading: boolean = false;

  public isInMessagerie = false;

  public positions: Formation[] | undefined;

  public formationsGroups: FormationsGroups[] = [];

  constructor(
    private navigationService: NavigationService,
    private profileService: ProfileService,
    private errorService: ErrorService,
    private paramedicalService: ParamedicalService,
    private userService: UserService,
    public dialogRef: MatDialogRef<AnnuairePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  async ngOnInit() {
    this.positions = [];
    const allFormations = await this.userService.getAllFormations();
    await Promise.all([
      await this.userService.getFormationsByGroupName(allFormations, 'NURSES'),
      await this.userService.getFormationsByGroupName(allFormations, 'SURGEONS'),
      await this.userService.getFormationsByGroupName(allFormations, 'ANESTHESISTES'),
      await this.userService.getFormationsByGroupName(allFormations, 'CDB'),
      await this.userService.getFormationsByGroupName(allFormations, 'IADES'),
    ]).then((res: any) => {
      res.forEach((formations: Formation[]) => {
        this.positions = this.positions?.concat(formations);
      });
    });
    if (this.navigationService.getCurrentPage().substring(0, 11) === '/messagerie') {
      this.isInMessagerie = true;
    }
    this.formationsGroups = await this.userService.getAllFormationsGroups();
    this.getParamedicalData();
    this.getProfileList(false);
  }

  public doFilter = (value: string): void => {
    this.page = 1;
    this.searchValue = value;
    this.isLoading = true;
    this.getProfileList();
  };

  getProfileList(concat?: boolean) {
    this.subscriptionManager
      .createRequest(
        this.profileService.getProfiles(this.page, this.pageSize, this.searchValue, true, undefined, undefined, {
          noHospitalsLookup: true,
        }),
        'getProfiles',
        this.instanceId
      )
      .subscribe({
        next: (res: any) => {
          if (concat && this.profiles) {
            this.profiles = this.profiles.concat(res.docs);
          } else {
            this.profiles = res.docs;
          }
          this.profiles = this.profiles!.filter((profile: ExtendedProfile) => !profile.isDefaultSpecialtySurgeon);
          this.isLoading = false;
          this.scrollLoading = false;
          this.hasNextPage = res.hasNextPage;
          this.page++;
          this.pageSize = 10;
          this.profiles.forEach((p: ExtendedProfile) => {
            this.setButtons(p);
          });
        },
        error: (error: any) => {
          this.isLoading = false;
          this.errorService.handleError(error);
        },
      });
  }

  private hasMatchingSeniority(seniority: string[] | undefined, profileSeniority: string | undefined): boolean {
    if (!seniority || seniority.length === 0) {
      return true;
    }
    if (!profileSeniority || !profileSeniority.length) {
      return false;
    }

    return seniority.includes(profileSeniority);
  }

  private hasMatchingResidency(residency: string[] | undefined, profileResidency: string | undefined): boolean {
    if (!residency || residency.length === 0) {
      return true;
    }
    if (!profileResidency || !profileResidency.length) {
      return false;
    }

    return residency.includes(profileResidency);
  }

  private hasMatchingPosition(
    positionsInSearchBar: string[] | undefined,
    profilePosition: Formation | undefined
  ): boolean {
    if (!positionsInSearchBar || !profilePosition) {
      return false;
    }

    const byName = positionsInSearchBar.includes(profilePosition.name ?? '');
    const byGroup = this.formationsGroups?.some(
      (group: any) =>
        positionsInSearchBar.includes(group._id.toString()) &&
        group.formations.some((id: any) => id.toString() === (profilePosition._id?.toString() ?? ''))
    );

    return byName || byGroup;
  }

  setButtons(profile: ExtendedProfile) {
    const accessOptions = (this.userService.getCurrentUser().persona as Personas).accessOptions as AccessOptions[];
    const profilePosition = this.positions?.find((position: Formation) => position.name === profile.position);
    const getAccessOption = (url: string) => accessOptions.find((opt: AccessOptions) => opt.pageUrl === url);

    const profilesAccessOption = getAccessOption('/profiles');
    const smartPlanningAccessOption = getAccessOption('/smart-planning/my-program');
    const skillsAccessOption = getAccessOption('/parcours-ibode/competence/competence-individuelle');
    const statopAccessOptionSurgeons = getAccessOption('/statop/bloc/par-praticien');
    const statopAccessOptionAnesthesistes = getAccessOption('/statop/anesthesiste/par-anesthesiste');
    const jobDescriptionAccessOption = getAccessOption('/configuration/advanced/fiche-poste');

    const isSelf = profile._id === (this.userService.getCurrentUser().profile as Profile)._id;

    profile.shouldDisableProfileButton = !(
      profilesAccessOption &&
      (isSelf ||
        (profilesAccessOption.displaySearchBar &&
          this.hasMatchingPosition(profilesAccessOption.positionsInSearchBar, profilePosition) &&
          this.hasMatchingResidency(profilesAccessOption.residencyInSearchBar, profile.residency) &&
          this.hasMatchingSeniority(profilesAccessOption.seniorityInSearchBar, profile.seniority)))
    );

    profile.shouldDisableSmartplanningButton = !smartPlanningAccessOption;

    profile.shouldDisableSkillsButton = !(
      skillsAccessOption &&
      (isSelf ||
        (skillsAccessOption.displaySearchBar &&
          this.hasMatchingPosition(skillsAccessOption.positionsInSearchBar, profilePosition) &&
          this.hasMatchingResidency(skillsAccessOption.residencyInSearchBar, profile.residency) &&
          this.hasMatchingSeniority(skillsAccessOption.seniorityInSearchBar, profile.seniority)))
    );

    const isInGroup = (groupName: string) =>
      this.formationsGroups?.find(
        (group: any) =>
          group.name === groupName &&
          group.formations.some((id: any) => id.toString() === profilePosition?._id?.toString())
      );

    const isSurgeon = !!isInGroup('SURGEONS');
    const isAnesth = !!isInGroup('ANESTHESISTES');

    profile.shouldDisableStatopButton = !(
      (isSurgeon && statopAccessOptionSurgeons && (isSelf || statopAccessOptionSurgeons.displaySearchBar)) ||
      (isAnesth &&
        statopAccessOptionAnesthesistes &&
        (isSelf ||
          (statopAccessOptionAnesthesistes.displaySearchBar &&
            this.hasMatchingPosition(statopAccessOptionAnesthesistes.positionsInSearchBar, profilePosition) &&
            this.hasMatchingResidency(statopAccessOptionAnesthesistes.residencyInSearchBar, profile.residency) &&
            this.hasMatchingSeniority(statopAccessOptionAnesthesistes.seniorityInSearchBar, profile.seniority))))
    );

    profile.shouldDisableJobDescriptionButton = !(
      jobDescriptionAccessOption &&
      (isSelf ||
        (jobDescriptionAccessOption.displaySearchBar &&
          this.hasMatchingPosition(jobDescriptionAccessOption.positionsInSearchBar, profilePosition) &&
          this.hasMatchingResidency(jobDescriptionAccessOption.residencyInSearchBar, profile.residency) &&
          this.hasMatchingSeniority(jobDescriptionAccessOption.seniorityInSearchBar, profile.seniority)))
    );
  }

  getParamedicalData() {
    return new Promise<void>((resolve: any, reject: any) => {
      this.subscriptionManager
        .createRequest(this.paramedicalService.getParamedicals(), 'getParamedicals', this.instanceId)
        .subscribe({
          next: (paramedicals: Paramedical[]) => {
            this.paramedicalsData = paramedicals;
            resolve();
          },
          error: (error: any) => {
            this.errorService.handleError(error);
            reject();
          },
        });
    });
  }

  getEmailByUserOrProfile(profile: any): string {
    const associatedUser = profile.user;
    if (associatedUser && associatedUser.email) {
      {
        return associatedUser.email;
      }
    }
    if (profile && profile.mail) {
      {
        return profile.mail;
      }
    }
    return '';
  }

  getEmailByUserOrProfileTooltip(profile: any): string {
    const associatedUser = profile.user;
    if (associatedUser && associatedUser.email) {
      {
        return associatedUser.email;
      }
    }
    if (profile && profile.mail) {
      return profile.mail;
    }
    return '';
  }

  hasSkills(position: string | undefined): string {
    if (this.paramedicalsData && position) {
      for (const paramed of this.paramedicalsData) {
        if (paramed.formation?.name === position && paramed.roles) {
          for (const role of paramed.roles) {
            if (role.roleId.hasSkills) {
              return 'Compétences';
            }
          }
        }
      }
    }

    return "Suivi d'activité";
  }

  goToMessagerie(profile: Profile) {
    if (this.isInMessagerie === true || profile.position === 'Chirurgien') {
      return;
    }
    this.navigateTo(profile, '/messagerie');
  }

  navigateTo(data: Profile, page: string) {
    this.close();
    this.navigationService.navigateTo(page, { profile: JSON.stringify(data) }, false);
  }

  goToPage(profile: Profile, url: string) {
    this.close();

    const finalUrl = url + profile._id;
    this.navigationService.navigateByUrl(finalUrl);
  }

  navigateByUrl(page: string, profile: Profile) {
    this.close();
    const url = page + profile._id;
    this.navigationService.navigateByUrl(url);
  }

  close(): void {
    this.dialogRef.close();
  }

  onScroll(e: any) {
    const tableViewHeight = e.target.offsetHeight;
    const tableScrollHeight = e.target.scrollHeight;
    const scrollLocation = e.target.scrollTop;
    const buffer = 100;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit && this.hasNextPage && !this.scrollLoading) {
      this.scrollLoading = true;
      this.getProfileList(true);
    }
  }
}
